/**
 * useLifecycleBoot — Owns the boot sequence and visibility listener.
 * Extracted from RetreatBridge lines 338-385.
 */
import { useEffect, useRef, type Dispatch } from "react";
import { RetreatManager } from "@retreats/engine";
import { createChildLogger } from "@retreat/shared";
import { createVisibilityHandler } from "../lifecycle/bridgeHandlers";
import { setupDebugTools } from "../debug/setupDebugTools";
import { loadScheduledRetreat, loadLastUsedSchedule } from "../state/schedulingState";
import { demoRetreat } from "@retreats/content";
import type { UIAction } from "../bridge/types";
import { TabCoordinator } from "../services/TabCoordinator";
import { syncManager } from "../services/SyncManager";
import { UI_ACTION_TYPES, STORAGE_KEYS } from "../constants";
import { getTestSchedule, isE2ESkipProbe } from "../utils/env";

const log = createChildLogger("useLifecycleBoot");

/**
 * If probe() found a persisted session, show the Netflix-style resume card on the home screen.
 * Checks for another active tab first — if one is running, signals OTHER_TAB_ACTIVE
 * so the UI can tell the user instead of silently starting a parallel engine.
 * Does NOT call confirmResume — the user explicitly triggers that via the resume card.
 */
async function autoResumeIfPending(
  manager: RetreatManager,
  dispatchUI: Dispatch<UIAction>,
): Promise<void> {
  const pending = manager.getSnapshot().pendingSession;
  if (!pending) return;

  const activeInOtherTab = await TabCoordinator.get().queryOtherTabs();
  if (activeInOtherTab) {
    log.info("autoResumeIfPending: another tab is active — deferring to user");
    dispatchUI({ type: UI_ACTION_TYPES.OTHER_TAB_ACTIVE, retreatName: activeInOtherTab.retreatName ?? undefined });
    return;
  }

  log.info("autoResumeIfPending: pending session found — surfacing resume card");
  dispatchUI({ type: UI_ACTION_TYPES.RETURNING_START });
  // Engine stays in probed state; user resumes explicitly via the home-screen card.
}

async function selectProbeResume(
  manager: RetreatManager,
  schedule: any,
  dispatchUI: Dispatch<UIAction>,
): Promise<void> {
  manager.selectRetreat(schedule);
  await manager.probe();
  await autoResumeIfPending(manager, dispatchUI);
}

export function useLifecycleBoot(
  dispatchUI: Dispatch<UIAction>,
  demo: boolean,
  testEngine: boolean = false,
): void {
  const testEngineInitRef = useRef(false);

  useEffect(() => {
    let cleanupSyncListeners: (() => void) | null = null;
    const visibilityHandler = createVisibilityHandler(() =>
      RetreatManager.get(),
    );
    document.addEventListener("visibilitychange", visibilityHandler);

    setupDebugTools();

    const init = async () => {
      try {
        const manager = RetreatManager.get();

        log.info(
          "boot: mode=%s schedule=%s",
          isE2ESkipProbe() ? "skip-probe" : "probe",
          (window.__retreatTestSchedule as { id?: string } | null | undefined)?.id ?? "default",
        );

        // E2E navigation tests set __E2E_SKIP_PROBE__ to stay in IDLE (HomeScreen)
        // without triggering the probe/initialize sequence.
        if (isE2ESkipProbe()) {
          // Leave engine in IDLE — navigation tests need HomeScreen
        } else if (demo) {
          const testSchedule = getTestSchedule();
          await selectProbeResume(manager, testSchedule ?? demoRetreat, dispatchUI);
        } else {
          // Check for test schedule override (used in e2e tests)
          const testSchedule = getTestSchedule();
          if (testSchedule) {
            await selectProbeResume(manager, testSchedule, dispatchUI);
          } else {
            const scheduled = loadScheduledRetreat();
            if (scheduled) {
              await selectProbeResume(manager, scheduled.schedule, dispatchUI);
            } else {
              // Probe for "Start Now" retreats too — they're saved as lastUsedSchedule.
              // Without this, a page refresh clears the retreat and lets a second one start.
              const lastUsed = loadLastUsedSchedule();
              if (lastUsed) {
                await selectProbeResume(manager, lastUsed, dispatchUI);
              }
            }
          }
        }

        // Register lifecycle listeners (online, visibilitychange) and do startup drain.
        cleanupSyncListeners = syncManager.init();
        void syncManager.drain();

        log.info("RetreatBridge initialized");
      } catch (err) {
        log.info("Error during init: %s", String(err));
      } finally {
        dispatchUI({ type: "BOOT_COMPLETE" });
        // Skip preparation screen for testEngine after boot completes
        if (testEngine && !testEngineInitRef.current) {
          testEngineInitRef.current = true;
          localStorage.setItem(STORAGE_KEYS.PREPARATION_SEEN, "true");
          dispatchUI({ type: "PREPARATION_SEEN" });
        }
      }
    };
    init();

    return () => {
      document.removeEventListener("visibilitychange", visibilityHandler);
      cleanupSyncListeners?.();
    };
  }, [demo, dispatchUI, testEngine]);
}
